import { useState, useDeferredValue, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { LuRefreshCw } from 'react-icons/lu';
import SharedJobPostListRow from './SharedJobPostListRow';
import useSyncSharedJobPostList from '../hooks/useSyncSharedJobPostList';
import useSyncSharedJobPostListItem from '../hooks/useSyncSharedJobPostListItem';

export interface SharedJobPostList {
  id: number;
  name: string;
  provider: string;
  sync_status: string;
  last_imported_at: string | null;
  owner_login_failed: boolean;
  owner_login_id: string | null;
}

interface ProviderSyncInfo {
  id: number;
  provider: string;
  sync_status: string;
  last_synced_at: string | null;
}

interface ProviderOption {
  key: string;
  label: string;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lists: SharedJobPostList[];
  providers: ProviderOption[];
  providerSyncInfos: ProviderSyncInfo[];
  onSelectList: (listId: string) => void;
}

export default function SharedJobPostListFilterDialog({
  open,
  onOpenChange,
  lists,
  providers,
  providerSyncInfos,
  onSelectList,
}: Props) {
  const [selectedProviderKey, setSelectedProviderKey] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredQuery = useDeferredValue(searchQuery);
  const [syncing, setSyncing] = useState(false);
  const syncMutation = useSyncSharedJobPostList();
  const syncListItemMutation = useSyncSharedJobPostListItem();

  // リストが1件以上あるプロバイダーのみ表示
  const providersWithLists = useMemo(() => {
    return providers.filter((p) => lists.some((l) => l.provider === p.label));
  }, [providers, lists]);

  // ダイアログが開いた瞬間だけ初期化
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSearchQuery('');
      if (providersWithLists.length > 0) {
        setSelectedProviderKey(providersWithLists[0].key);
      }
    }
    prevOpenRef.current = open;
  }, [open, providersWithLists]);

  // 選択中プロバイダー＆検索クエリでフィルタ
  const filteredLists = useMemo(() => {
    let result = lists;
    const selectedProvider = providers.find((p) => p.key === selectedProviderKey);
    if (selectedProvider) {
      result = result.filter((l) => l.provider === selectedProvider.label);
    }
    if (deferredQuery) {
      result = result.filter((l) => l.name.toLowerCase().includes(deferredQuery.toLowerCase()));
    }
    return result;
  }, [lists, providers, selectedProviderKey, deferredQuery]);

  // 選択中プロバイダーの最終同期日（OrganizationSharedJobPostProvider から取得）
  const lastSyncedAt = useMemo(() => {
    if (!selectedProviderKey) return null;
    const syncInfo = providerSyncInfos.find((p) => p.provider === selectedProviderKey);
    if (!syncInfo?.last_synced_at) return null;
    return new Date(syncInfo.last_synced_at);
  }, [providerSyncInfos, selectedProviderKey]);

  // 選択中プロバイダーがサーバー側で同期中かどうか
  const isSyncingOnServer = useMemo(() => {
    if (!selectedProviderKey) return false;
    const syncInfo = providerSyncInfos.find((p) => p.provider === selectedProviderKey);
    return syncInfo?.sync_status === 'syncing';
  }, [providerSyncInfos, selectedProviderKey]);

  // 選択中プロバイダー内で、いずれかのリストが同期中かどうか
  const isAnyListSyncing = useMemo(
    () => filteredLists.some((list) => list.sync_status === 'syncing'),
    [filteredLists]
  );

  const formatDate = (date: Date) => {
    return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
  };

  const handleSelectList = (listId: number) => {
    onSelectList(String(listId));
    onOpenChange(false);
  };

  // 選択中プロバイダーで同期を実行
  const handleSync = useCallback(async () => {
    if (!selectedProviderKey) return;

    setSyncing(true);
    try {
      await syncMutation.mutateAsync(selectedProviderKey);
    } catch {
      // エラーはmutationのonError等で処理可能。ここではsyncingフラグのリセットのみ
    } finally {
      setSyncing(false);
    }
  }, [selectedProviderKey, syncMutation]);

  // リスト単位の同期を実行
  const handleSyncList = useCallback(
    async (e: React.MouseEvent, listId: number) => {
      e.stopPropagation();
      try {
        await syncListItemMutation.mutateAsync(listId);
      } catch {
        // エラーはmutationのonError等で処理可能
      }
    },
    [syncListItemMutation]
  );

  const isSyncDisabled = syncing || isSyncingOnServer || isAnyListSyncing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[860px] rounded-sm">
        <DialogHeader className="border-b-0 px-lg pt-md pb-0">
          <DialogTitle className="text-base font-bold">リストで絞り込む</DialogTitle>
        </DialogHeader>

        {/* プロバイダー切り替えタブ */}
        {providersWithLists.length > 0 && (
          <div className="flex">
            {providersWithLists.map((provider) => {
              const isSelected = selectedProviderKey === provider.key;
              return (
                <button
                  key={provider.key}
                  type="button"
                  className={`flex h-[44px] max-w-[300px] flex-1 cursor-pointer items-center justify-center border-0 border-solid bg-transparent text-sm transition-colors ${
                    isSelected
                      ? 'border-b-2 border-primary font-bold text-primary'
                      : 'border-b border-border font-normal text-main hover:text-primary'
                  }`}
                  onClick={() => setSelectedProviderKey(provider.key)}
                >
                  {provider.label}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-col gap-sm px-lg py-md">
          {/* リスト検索 + 同期 */}
          <div className="flex items-center gap-2">
            <div className="min-w-0 basis-3/5">
              <Input
                search
                placeholder="リストを検索"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full"
              />
            </div>
            <div className="ml-auto flex items-center gap-2">
              {lastSyncedAt && (
                <span className="shrink-0 text-xs text-sub">
                  最終同期日：{formatDate(lastSyncedAt)}
                </span>
              )}
              <Button
                color="tertiary"
                size="sm"
                type="button"
                disabled={isSyncDisabled}
                onClick={handleSync}
              >
                <LuRefreshCw
                  className={`size-4 ${
                    // 実際にプロバイダー単位の同期が進行中のときだけスピンさせる
                    syncing || isSyncingOnServer ? 'animate-spin' : ''
                  }`}
                />
                同期
              </Button>
            </div>
          </div>

          {/* リスト一覧 */}
          <div className="h-[400px] overflow-y-auto rounded">
            {filteredLists.length > 0 ? (
              filteredLists.map((list) => (
                <SharedJobPostListRow
                  key={list.id}
                  list={list}
                  isSyncingOnServer={isSyncingOnServer}
                  lastSyncedAt={lastSyncedAt}
                  formatDate={formatDate}
                  onSelectList={handleSelectList}
                  onSyncList={handleSyncList}
                />
              ))
            ) : (
              <div className="py-8 text-center text-sm text-sub">リストが見つかりません</div>
            )}
          </div>
        </div>

        <DialogFooter className="px-md py-sm">
          <Button color="secondary" size="md" type="button" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
